import { ThemeCssUnit, ThemeFontStyle, ThemeTextTransform } from '../../../enums/css.enum';
import { ThemeFontWeights } from '../../../enums/font-families.enum';
import type { ThemeFont, ThemeResponsive } from '../base';
import { toCssSize } from './css-size';
import type { WebsiteThemeFontLegacy, WebsiteThemeFontStylesLegacy } from './font';

/** The three bags the legacy shape stored, in the order v0's `desktop`/`tablet`/`mobile` expects. */
const BREAKPOINTS = ['desktop', 'tablet', 'mobile'] as const;

type Breakpoint = (typeof BREAKPOINTS)[number];

const LEGACY_BAG: Readonly<
    Record<Breakpoint, keyof Pick<WebsiteThemeFontLegacy, 'styles' | 'tabletStyles' | 'mobileStyles'>>
> = {
    desktop: 'styles',
    tablet: 'tabletStyles',
    mobile: 'mobileStyles',
};

/** `'light' | 'normal' | 'bold'` as the presets wrote them; the panels wrote the numbers directly. */
const NAMED_WEIGHTS: Readonly<Record<ThemeFontWeights, number>> = {
    [ThemeFontWeights.light]: 300,
    [ThemeFontWeights.normal]: 400,
    [ThemeFontWeights.bold]: 700,
};

/** `300 | 400 | 700`, whether it was stored as the number or as `'light' | 'normal' | 'bold'`. */
function toFontWeight(raw: unknown): number | undefined {
    if (typeof raw === 'number') {
        return Number.isFinite(raw) ? raw : undefined;
    }

    if (typeof raw !== 'string') {
        return undefined;
    }

    const named = NAMED_WEIGHTS[raw as ThemeFontWeights];

    if (named !== undefined) {
        return named;
    }

    const parsed = Number.parseInt(raw, 10);

    return Number.isFinite(parsed) ? parsed : undefined;
}

/** A stored string, kept only if it is one of the values the property's enum actually names. */
function toEnumValue<T extends string>(raw: unknown, values: readonly T[]): T | undefined {
    return typeof raw === 'string' && (values as readonly string[]).includes(raw) ? (raw as T) : undefined;
}

/** A non-empty string — a cleared `fontFamily` is `null` or `''`, and neither is a CSS stack. */
function toCssString(raw: unknown): string | undefined {
    return typeof raw === 'string' && raw.trim() !== '' ? raw : undefined;
}

/**
 * One property across the three bags, or `undefined` if no breakpoint had a usable value.
 *
 * Returning `undefined` rather than an empty object matters: `fontSize: {}` and no `fontSize` mean
 * the same thing to a reader, and only one of them survives a round trip through `JSON.stringify`
 * unchanged.
 */
function toResponsive<T>(font: WebsiteThemeFontLegacy, read: (bag: WebsiteThemeFontStylesLegacy) => T | undefined) {
    const responsive: ThemeResponsive<T> = {};

    BREAKPOINTS.forEach((breakpoint) => {
        const bag = font[LEGACY_BAG[breakpoint]];
        const value = bag ? read(bag) : undefined;

        if (value !== undefined) {
            responsive[breakpoint] = value;
        }
    });

    return Object.keys(responsive).length > 0 ? responsive : undefined;
}

const FONT_STYLES = Object.values(ThemeFontStyle);
const TEXT_TRANSFORMS = Object.values(ThemeTextTransform);

/**
 * Legacy font → v0 font: breakpoint-first to property-first, with every value given a type.
 *
 * The inversion is mechanical, but the narrowing is not — this is where `'16px'` becomes
 * `{ value: 16, unit: 'px' }` and `'bold'` becomes `700`. Values that fit none of the new shapes are
 * dropped, on the same reasoning throughout this file: an absent property renders as the browser
 * default, whereas a guessed one renders as something the user never chose.
 */
export function migrateWebsiteThemeFontLegacy(font: WebsiteThemeFontLegacy): ThemeFont {
    return {
        tag: font.tag,
        font: font.font,
        fontFamily: toResponsive(font, (bag) => toCssString(bag.fontFamily)),
        fontSize: toResponsive(font, (bag) => toCssSize(bag.fontSize, ThemeCssUnit.px)),
        fontWeight: toResponsive(font, (bag) => toFontWeight(bag.fontWeight)),
        fontStyle: toResponsive(font, (bag) => toEnumValue(bag.fontStyle, FONT_STYLES)),
        lineHeight: toResponsive(font, (bag) => toCssSize(bag.lineHeight, ThemeCssUnit.percent)),
        textTransform: toResponsive(font, (bag) => toEnumValue(bag.textTransform, TEXT_TRANSFORMS)),
        letterSpacing: toResponsive(font, (bag) => toCssSize(bag.letterSpacing, ThemeCssUnit.px)),
        color: toResponsive(font, (bag) => toCssString(bag.color)),
    };
}

/**
 * The device bags as the legacy readers actually rendered them, before they are inverted.
 *
 * Synchronised (and a site that predates the flag counts as synchronised) means every breakpoint
 * mirrors desktop, so the tablet and mobile bags are dropped and v0 stores desktop alone — which
 * also discards the stale per-device values an earlier adapter bug persisted on synchronised sites.
 * The CSS variables spell the breakpoints back out (`formatThemeFontProperty`).
 *
 * Desynchronised keeps what was saved and fills a genuinely missing bag the way the legacy adapter
 * rendered it: tablet ← mobile, and mobile ← desktop. The second is written out only beside a tablet
 * bag: v0's own fallback runs the other way round (mobile ← tablet), so a site with a tablet
 * override but no mobile one would otherwise start rendering the tablet values on phones.
 */
export function normalizeLegacyFontDevices(
    font: WebsiteThemeFontLegacy,
    isSynchronized: boolean
): WebsiteThemeFontLegacy {
    const { tabletStyles, mobileStyles, ...desktopOnly } = font;

    if (isSynchronized) {
        return desktopOnly;
    }

    const tablet = tabletStyles ?? mobileStyles;
    // Only needed when tablet has a bag of its own: without one, mobile reads desktop either way.
    const mobile = mobileStyles ?? (tablet ? font.styles : undefined);

    return {
        ...desktopOnly,
        ...(tablet ? { tabletStyles: tablet } : {}),
        ...(mobile ? { mobileStyles: mobile } : {}),
    };
}
